import { plural, t } from '@lingui/core/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { SearchParamsPetType } from '@rover/constants/src/translations/sitterProfilePage.constants';
import type { Icon as IconType } from '@rover/icons';
import Calendar from '@rover/icons/svg/calendar.svg';
import Rebook from '@rover/icons/svg/rebook.svg';
import RecurringCalendar from '@rover/icons/svg/recurring-calendar.svg';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import type {
  EnhancedPersonSitterProfileServiceAddOn as AddonType,
  EnhancedPersonSitterProfileServiceAddOn,
} from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import { PetType } from '@rover/types/src/Pet';
import {
  ALLOWED_ADDITIONAL_RATES_FOR_SERVICE_TYPE,
  CAT_ONLY_ALLOWED_ADD_ONS,
  isAdditionalRateAddOn,
  ServiceAddOnTypeSlugs as AddOnSlugs,
} from '@rover/types/src/ServiceAddOn';
import { formatCurrency } from '@rover/utilities/currency';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

export type AcceptingNewCustomerProps = {
  text: string;
  icon: IconType;
};

export const getAcceptingNewCustomersLabel = (
  service: EnhancedPersonSitterProfileService
): AcceptingNewCustomerProps | null => {
  if (service.isAway) {
    return null;
  }

  if (service.isRepeatCustomersOnly) {
    return {
      text: t`Not accepting new customers.`,
      icon: Rebook,
    };
  }

  if (service.isInRecurringServiceListingPreferencesRollout) {
    return null;
  }

  if (service.isAcceptingNewOneTimeCustomersOnly) {
    return {
      text: t`Not accepting new customers for weekly care.`,
      icon: RecurringCalendar,
    };
  }
  if (service.isAcceptingNewRecurringClientsOnly) {
    return {
      text: t`Not accepting new customers for one-time care.`,
      icon: Calendar,
    };
  }

  return null;
};

export const formatCreditsPrice = (priceInCredits: string): string => {
  return plural(priceInCredits, {
    one: '# credit',
    other: '# credits',
  });
};

export const formatPrice = (
  addnOn: AddonType,
  locale: Locale,
  isFullPrice: boolean = false,
  priceInCredits: string | null = null
): string => {
  if (priceInCredits) {
    return formatCreditsPrice(priceInCredits);
  }

  const { slug, pricePerUnitObj, fullPricePerUnit } = addnOn;
  if (slug === 'extended-care') return t`50-100%`;

  const amount = isFullPrice && fullPricePerUnit ? fullPricePerUnit : pricePerUnitObj.amount;
  const priceLabel =
    addnOn.priceLabel ||
    formatCurrency(amount, pricePerUnitObj.currencyIso, locale, {
      includeDecimal: false,
    });
  return isAdditionalRateAddOn(slug as AddOnSlugs) ? `+ ${priceLabel}` : priceLabel;
};

/*
  This function filters the addons to be shown in the service detail section, depending on the pet type (undefined = any).
  The standard rate and costAdjustments are shown in other places, so we have to filter.
*/
export const filterAddons = (
  addons: EnhancedPersonSitterProfileServiceAddOn[],
  petType: SearchParamsPetType | undefined,
  availablePetTypes: PetType[],
  isCatOnlyService: boolean,
  serviceSlug?: string,
  isSitterInPetCareModesExperiment = false
): EnhancedPersonSitterProfileServiceAddOn[] => {
  const isCatOnly = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePetTypes);
  const allowedForServiceType =
    isSitterInPetCareModesExperiment && serviceSlug
      ? ALLOWED_ADDITIONAL_RATES_FOR_SERVICE_TYPE.get(serviceSlug)
      : undefined;

  return addons.filter((s) => {
    // These add-ons are not shown in the service detail section
    const addOnsToExclude = [
      AddOnSlugs.standardRate,
      AddOnSlugs.costAdjustments,
      AddOnSlugs.missingServiceDeliveries,
    ];
    const slug = s.slug as AddOnSlugs;
    const shouldNotExcludeAddOn = !addOnsToExclude.includes(slug);

    // check if slug is valid
    const isValidSlug = Object.values(AddOnSlugs).includes(slug);

    // check if addon is for searched pet type
    const isOfSuggestedPetType = [petType, 'any'].includes(s.suggestedPetType);

    // For cat-only brands, filter out catCare (it's the standard rate) and only show allowed add-ons
    if ((isCatOnly || isCatOnlyService) && !CAT_ONLY_ALLOWED_ADD_ONS.includes(slug)) {
      return false;
    }

    // When the sitter being viewed is in the SSU experiment, only show add-ons allowed for this service type
    if (allowedForServiceType && !allowedForServiceType.includes(slug)) {
      return false;
    }

    return (
      s.active &&
      shouldNotExcludeAddOn &&
      isValidSlug &&
      (!petType ||
        petType === SearchParamsPetType.DOG_CAT ||
        petType === SearchParamsPetType.CAT_DOG ||
        isOfSuggestedPetType)
    );
  });
};
